import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { buildApiUrl } from './api';
import { useSEO } from './useSEO';

// ─── Polling helper ─────────────────────────────────────────────────────────
function usePollSubmission(submissionId, token) {
  const [data, setData] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!submissionId || !token) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await fetch(buildApiUrl(`/api/ai-marker/submissions/${submissionId}`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled) setData(json);
        if (json.status === 'processing') {
          timer.current = setTimeout(poll, 2000);
        }
      } catch { /* retry on next tick */ }
    };
    poll();

    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [submissionId, token]);

  return data;
}

// ─── File upload helper component ───────────────────────────────────────────
function ImageUpload({ label, sublabel, files, setFiles, maxFiles = 8 }) {
  const ref = useRef(null);
  const add = (e) => {
    const incoming = Array.from(e.target.files || []);
    if (incoming.length) setFiles((prev) => [...prev, ...incoming].slice(0, maxFiles));
    if (ref.current) ref.current.value = '';
  };
  const remove = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div className="aim-field">
      <label>{label} {sublabel && <span className="muted">{sublabel}</span>}</label>
      <div className="aim-drop">
        <input ref={ref} type="file" multiple accept="image/png,image/jpeg,image/gif,image/webp" onChange={add} />
        <p className="muted">PNG, JPG, GIF or WebP — max {maxFiles} images, 10 MB each</p>
      </div>
      {files.length > 0 && (
        <div className="aim-image-list">
          {files.map((f, i) => (
            <div key={`${f.name}-${i}`} className="aim-image-chip">
              <span>{f.name}</span>
              <button onClick={() => remove(i)} className="aim-image-remove">✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────
export default function AIMarker() {
  useSEO({
    title: 'AI Marker',
    description: 'Submit a hand-written exam answer and get instant AI feedback against the official mark scheme.',
    canonicalPath: '/ai-marker',
  });
  const { user, token } = useAuth();

  // Step: 1=select paper, 2=enter question+answer, 3=processing, 4=result
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');

  // Options tree from backend
  const [options, setOptions] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Selections
  const [level, setLevel] = useState('');
  const [subject, setSubject] = useState('');
  const [board, setBoard] = useState('');
  const [paper, setPaper] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Question input
  const [questionText, setQuestionText] = useState('');
  const [questionImages, setQuestionImages] = useState([]);
  const [maxMarks, setMaxMarks] = useState('');

  // Answer input
  const [typedAnswer, setTypedAnswer] = useState('');
  const [answerImages, setAnswerImages] = useState([]);

  // Mark scheme (optional)
  const [markschemeImages, setMarkschemeImages] = useState([]);

  // Submission
  const [submissionId, setSubmissionId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Poll result
  const result = usePollSubmission(submissionId, token);

  useEffect(() => {
    if (result && result.status === 'completed') setStep(4);
    if (result && result.status === 'failed') {
      setError(result.errorMessage || 'Marking failed');
      setStep(2);
    }
  }, [result]);

  // ── Load options ──
  const fetchOptions = useCallback(async () => {
    if (!token) return;
    setLoadingOptions(true);
    try {
      const res = await fetch(buildApiUrl('/api/ai-marker/options'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOptions(data.options || {});
      }
    } catch { /* ignored */ }
    setLoadingOptions(false);
  }, [token]);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);

  // ── Derived option lists ──
  const levels = options ? Object.keys(options) : [];
  const subjects = (level && options?.[level]) ? Object.keys(options[level]) : [];
  const boards = (level && subject && options?.[level]?.[subject]) ? Object.keys(options[level][subject]) : [];
  const papers = (level && subject && board && options?.[level]?.[subject]?.[board]) ? Object.keys(options[level][subject][board]) : [];
  const paperInfo = (level && subject && board && paper) ? options?.[level]?.[subject]?.[board]?.[paper] : null;

  // ── Handlers ──
  const handleSelectLevel = (v) => { setLevel(v); setSubject(''); setBoard(''); setPaper(''); setSelectedTemplate(null); };
  const handleSelectSubject = (v) => { setSubject(v); setBoard(''); setPaper(''); setSelectedTemplate(null); };
  const handleSelectBoard = (v) => { setBoard(v); setPaper(''); setSelectedTemplate(null); };
  const handleSelectPaper = (v) => {
    setPaper(v);
    const info = options?.[level]?.[subject]?.[board]?.[v];
    setSelectedTemplate(info || null);
  };

  const hasQuestion = (questionText && questionText.trim().length > 0) || questionImages.length > 0;
  const hasAnswer = (typedAnswer && typedAnswer.trim().length > 0) || answerImages.length > 0;
  const parsedMaxMarks = parseInt(maxMarks, 10);
  const hasMaxMarks = !isNaN(parsedMaxMarks) && parsedMaxMarks >= 1;
  const canSubmit = !!(selectedTemplate && hasQuestion && hasAnswer && hasMaxMarks);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError('');
    setSubmitting(true);
    setStep(3);

    try {
      const fd = new FormData();
      fd.append('templateId', selectedTemplate.templateId);
      fd.append('maxMarks', parseInt(maxMarks, 10));
      if (questionText.trim()) fd.append('questionText', questionText.trim());
      if (typedAnswer.trim()) fd.append('typedAnswer', typedAnswer.trim());
      questionImages.forEach((f) => fd.append('questionImages', f));
      answerImages.forEach((f) => fd.append('answerImages', f));
      markschemeImages.forEach((f) => fd.append('markschemeImages', f));

      const res = await fetch(buildApiUrl('/api/ai-marker/submissions'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Server returned ${res.status}`);
      }

      const data = await res.json();
      setSubmissionId(data.submissionId);
    } catch (err) {
      setError(err.message || 'Submission failed');
      setStep(2);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartOver = () => {
    setStep(1);
    setError('');
    setLevel('');
    setSubject('');
    setBoard('');
    setPaper('');
    setSelectedTemplate(null);
    setQuestionText('');
    setQuestionImages([]);
    setMaxMarks('');
    setTypedAnswer('');
    setAnswerImages([]);
    setMarkschemeImages([]);
    setSubmissionId(null);
  };

  const handleNewAnswer = () => {
    setStep(2);
    setError('');
    setTypedAnswer('');
    setAnswerImages([]);
    setMarkschemeImages([]);
    setSubmissionId(null);
  };

  // ── Auth guard ──
  if (!user) {
    return (
      <div className="auth-wrapper">
        <div className="auth-card">
          <p>Please <a href="/login">log in</a> to use the AI Marker.</p>
        </div>
      </div>
    );
  }

  // ── Render ──
  return (
    <div className="aim-page">
      <h1 className="aim-title">AI Exam Marker</h1>
      <p className="aim-subtitle">Upload any question and get it marked instantly with detailed feedback.</p>

      {error && <div className="aim-error">{error}</div>}

      {/* ── STEP 1: Select paper ── */}
      {step === 1 && (
        <div className="aim-section">
          {loadingOptions ? (
            <p className="muted">Loading available papers…</p>
          ) : levels.length === 0 ? (
            <p className="muted">No marking templates are available yet. Check back soon!</p>
          ) : (
            <>
              <h3>1. Choose your exam paper</h3>

              <div className="aim-field">
                <label>Level</label>
                <div className="aim-chips">
                  {levels.map((l) => (
                    <button key={l} className={`aim-chip ${level === l ? 'active' : ''}`} onClick={() => handleSelectLevel(l)}>{l}</button>
                  ))}
                </div>
              </div>

              {level && (
                <div className="aim-field">
                  <label>Subject</label>
                  <div className="aim-chips">
                    {subjects.map((s) => (
                      <button key={s} className={`aim-chip ${subject === s ? 'active' : ''}`} onClick={() => handleSelectSubject(s)}>{s}</button>
                    ))}
                  </div>
                </div>
              )}

              {subject && (
                <div className="aim-field">
                  <label>Exam Board</label>
                  <div className="aim-chips">
                    {boards.map((b) => (
                      <button key={b} className={`aim-chip ${board === b ? 'active' : ''}`} onClick={() => handleSelectBoard(b)}>{b}</button>
                    ))}
                  </div>
                </div>
              )}

              {board && (
                <div className="aim-field">
                  <label>Paper</label>
                  <div className="aim-chips">
                    {papers.map((p) => (
                      <button key={p} className={`aim-chip ${paper === p ? 'active' : ''}`} onClick={() => handleSelectPaper(p)}>{p}</button>
                    ))}
                  </div>
                </div>
              )}

              {paperInfo?.guidance && (
                <div className="aim-guidance">
                  <h4>Paper guidance</h4>
                  <p>{paperInfo.guidance}</p>
                </div>
              )}

              {selectedTemplate && (
                <button className="aim-btn primary" onClick={() => setStep(2)}>
                  Continue →
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* ── STEP 2: Enter question + answer ── */}
      {step === 2 && (
        <div className="aim-section">
          <div className="aim-step-header">
            <h3>2. Enter your question &amp; answer</h3>
            <p className="muted">{board} · {paper} · {subject}</p>
          </div>

          {/* Question */}
          <div className="aim-group">
            <h4>The Question</h4>
            <div className="aim-field">
              <label>Type the question <span className="muted">(or upload an image below)</span></label>
              <textarea
                className="aim-textarea"
                rows={4}
                placeholder="e.g. Discuss the working memory model. Refer to evidence in your answer. [16 marks]"
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
              />
            </div>

            <ImageUpload
              label="Upload question image(s)"
              sublabel="(screenshot or photo of the question)"
              files={questionImages}
              setFiles={setQuestionImages}
              maxFiles={4}
            />

            <div className="aim-field aim-field-inline">
              <label>Total marks available</label>
              <input
                type="number"
                className="aim-input-marks"
                min="1"
                max="100"
                placeholder="e.g. 16"
                value={maxMarks}
                onChange={(e) => setMaxMarks(e.target.value)}
              />
            </div>
          </div>

          {/* Mark scheme (optional) */}
          <div className="aim-group">
            <h4>Mark Scheme <span className="aim-optional-badge">Optional</span></h4>
            <p className="muted">Upload the mark scheme for more accurate marking. Without it, the AI will mark using its knowledge of {board} standards.</p>
            <ImageUpload
              label="Upload mark scheme image(s)"
              sublabel=""
              files={markschemeImages}
              setFiles={setMarkschemeImages}
              maxFiles={8}
            />
          </div>

          {/* Answer */}
          <div className="aim-group">
            <h4>Your Answer</h4>
            <div className="aim-field">
              <label>Type your answer <span className="muted">(or upload images below)</span></label>
              <textarea
                className="aim-textarea"
                rows={10}
                placeholder="Type your answer here…"
                value={typedAnswer}
                onChange={(e) => setTypedAnswer(e.target.value)}
              />
            </div>

            <ImageUpload
              label="Upload answer image(s)"
              sublabel="(photos of handwritten work)"
              files={answerImages}
              setFiles={setAnswerImages}
              maxFiles={8}
            />
          </div>

          <div className="aim-actions">
            <button className="aim-btn secondary" onClick={() => setStep(1)}>← Back</button>
            <button className="aim-btn primary" disabled={!canSubmit || submitting} onClick={handleSubmit}>
              {submitting ? 'Submitting…' : 'Submit for marking'}
            </button>
          </div>
          {!canSubmit && (
            <p className="aim-validation-hint muted">
              {!hasQuestion && 'Type the question or upload a question image. '}
              {!hasMaxMarks && 'Enter the total marks. '}
              {!hasAnswer && 'Type your answer or upload answer images.'}
            </p>
          )}
        </div>
      )}

      {/* ── STEP 3: Processing ── */}
      {step === 3 && (
        <div className="aim-section aim-center">
          <div className="aim-spinner" />
          <h3>Marking your answer…</h3>
          <p className="muted">This usually takes 10–30 seconds. Please don't close this page.</p>
        </div>
      )}

      {/* ── STEP 4: Result ── */}
      {step === 4 && result?.result && (
        <div className="aim-section">
          <h3>Your result</h3>

          <div className="aim-score-card">
            <div className="aim-score-big">
              <span className="aim-score-num">{result.result.awardedMarks}</span>
              <span className="aim-score-sep">/</span>
              <span className="aim-score-max">{result.result.maxMarks}</span>
            </div>
            <p className="aim-score-pct">
              {Math.round((result.result.awardedMarks / result.result.maxMarks) * 100)}%
            </p>
          </div>

          {/* Breakdown */}
          {result.result.breakdown?.markBreakdown?.length > 0 && (
            <div className="aim-breakdown">
              <h4>Mark Breakdown</h4>
              <table className="aim-table">
                <thead>
                  <tr>
                    <th>Criterion</th>
                    <th>Marks</th>
                    <th>Rationale</th>
                  </tr>
                </thead>
                <tbody>
                  {result.result.breakdown.markBreakdown.map((b, i) => (
                    <tr key={i}>
                      <td>{b.criterion}</td>
                      <td className="aim-mark-cell">{b.marksAwarded}/{b.marksAvailable}</td>
                      <td>{b.rationale}{b.evidence ? <em className="aim-evidence"> — "{b.evidence}"</em> : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Strengths */}
          {result.result.breakdown?.strengths?.length > 0 && (
            <div className="aim-feedback-box aim-strengths">
              <h4>Strengths</h4>
              <ul>{result.result.breakdown.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}

          {/* Improvements */}
          {result.result.breakdown?.improvements?.length > 0 && (
            <div className="aim-feedback-box aim-improvements">
              <h4>Areas for Improvement</h4>
              <ul>{result.result.breakdown.improvements.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}

          {/* Summary */}
          {result.result.feedbackSummary && (
            <div className="aim-feedback-box aim-summary">
              <h4>Examiner Summary</h4>
              <p>{result.result.feedbackSummary}</p>
            </div>
          )}

          <div className="aim-actions">
            <button className="aim-btn secondary" onClick={handleNewAnswer}>Try a different answer</button>
            <button className="aim-btn primary" onClick={handleStartOver}>Start over</button>
          </div>
        </div>
      )}
    </div>
  );
}
